import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { usePrevious } from 'react-use';
import { Row, Col, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { ValueType } from 'react-select/src/types';
import 'core-js/es/object/entries';
import { useNavigate } from 'react-router-dom';

import { Form } from '../../components/Form';
import {
  DatePicker,
  Label,
  OptionType,
  Select,
  TextInput,
} from '../../components/Input';
import { Button, ButtonLink } from '../../components/Button';
import { PrivacyMessage } from '../../components/PrivacyMessage';
import { SideBar } from '../../components/SideBar';
import { Text } from '../../components/Typography';
import { Section } from '../../components/Section';
import { Preferences } from './Preferences';
import { ProvinceSelector } from '../../components/ProvinceSelector';

import { getUser } from '../../selectors/user';
import { userUpdateRequest } from '../../actions/user';

import classes from './profile.module.scss';
import { api } from '../../utils/api';
import * as React from 'react';
import qs from 'qs';

type SexOption = {
  label: string;
  value: 'm' | 'f';
};

enum UpdateType {
  UPDATE_REQUEST,
  MARKETING_UPDATE_REQUEST,
}

export const Profile: FunctionComponent = () => {
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const { id: userId, ...userData } = user.data!;
  const [updateType, setUpdateType] = useState<UpdateType | undefined>();
  const navigate = useNavigate();

  const [fields, setFields] = useState({
    ...userData,
    password: '',
    privacyChecked: userData.privacyChecked,
  });
  const [formValidated, setFormValidated] = useState(false);
  const [showDeletionConfirmation, setShowDeletionConfirmation] =
    useState(false);
  const [showCouponOffer, setShowCouponOffer] = useState(false);
  const prevUser = usePrevious(user);

  const deleteAccount = useCallback(() => {
    console.log('Delete account request');
  }, []);

  const handleInputChange = useCallback(
    ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      const { name } = target;
      const value = target.type === 'checkbox' ? target.checked : target.value;

      setFields((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    []
  );

  const handleBirthdayChange = useCallback((date) => {
    setFields((prevState) => ({
      ...prevState,
      birthday: date,
    }));
  }, []);

  const handlePreferencesUpdate = useCallback((data: string[]) => {
    setFields((prevFields) => ({
      ...prevFields,
      data,
    }));
  }, []);

  const handleSexUpdate = useCallback(
    (option: ValueType<OptionType, boolean>) => {
      setFields((prevFields) => ({
        ...prevFields,
        sex: (option as SexOption).value,
      }));
    },
    []
  );

  const handleProvinceUpdate = useCallback((option: string) => {
    setFields((prevFields) => ({
      ...prevFields,
      province: option,
    }));
  }, []);

  const handleNewsletter = useCallback((checked: boolean) => {
    setFields((prevFields) => ({
      ...prevFields,
      newsletterChecked: checked,
      marketingChecked: checked,
    }));
  }, []);

  const toggleCouponOffer = useCallback(() => {
    setShowCouponOffer((prevShowCouponOffer) => !prevShowCouponOffer);
  }, []);

  const toggleDeletionConfirmation = useCallback(() => {
    setShowDeletionConfirmation(
      (prevShowDeletionConfirmation) => !prevShowDeletionConfirmation
    );
  }, []);

  const update = useCallback(
    (event: React.ChangeEvent<HTMLFormElement>) => {
      event.preventDefault();

      const form = event.currentTarget;

      if (!form.checkValidity()) {
        setFormValidated(true);
        event.stopPropagation();
      } else {
        const { password, ...update } = fields;

        setUpdateType(UpdateType.UPDATE_REQUEST);

        dispatch(
          userUpdateRequest({
            ...update,
            ...(password.length > 0 && { password }),
          })
        );
      }
    },
    [dispatch, fields]
  );

  const userIsEligibleForCoupon = useCallback(() => {
    return (
      fields.email.length > 0 &&
      fields.mobile.length > 0 &&
      user.isEligibleForCoupon
    );
  }, [fields, user.isEligibleForCoupon]);

  useEffect(() => {
    const { update_privacy, marketing_check } = qs.parse(
      window.top?.location.search.slice(1) || ''
    );

    if (
      update_privacy === '2' &&
      marketing_check === '1' &&
      (user.data!.privacyChecked !== '2' || !user.data!.marketingChecked)
    ) {
      dispatch(
        userUpdateRequest({
          privacyChecked: '2',
          marketingChecked: true,
          newsletterChecked: true,
        })
      );

      setUpdateType(UpdateType.MARKETING_UPDATE_REQUEST);
    }
  }, [dispatch, user.data, navigate]);

  useEffect(() => {
    const checkCoupon = async () => {
      if (
        prevUser &&
        prevUser.fetching &&
        !user.fetching &&
        !user.error &&
        userIsEligibleForCoupon()
      ) {
        try {
          const { data }: { data: Array<any> } = await api.get(
            `/user/${user.data!.id}/coupons/list`,
            {
              Authorization: `Bearer ${user.data!.token}`,
            }
          );

          if (data.length === 0 || process.env.NODE_ENV === 'development') {
            toggleCouponOffer();
          }
        } finally {
          setUpdateType(undefined);
        }
      }
    };

    if (updateType === UpdateType.UPDATE_REQUEST) {
      checkCoupon();
    } else if (updateType === UpdateType.MARKETING_UPDATE_REQUEST) {
      navigate('/coupons');
    }
  }, [
    prevUser,
    toggleCouponOffer,
    user.data,
    user.error,
    user.fetching,
    userIsEligibleForCoupon,
    updateType,
    navigate,
  ]);

  useEffect(() => {
    const { reoptin } = qs.parse(window.top?.location.search.slice(1) || '');

    if (reoptin === '1') {
      dispatch(
        userUpdateRequest({
          privacyChecked: '3',
        })
      );
    }
  }, []);

  return (
    <div>
      <SideBar>
        <Form
          autoComplete={'off'}
          noValidate
          validated={formValidated}
          className={classes.form}
          onSubmit={update}
        >
          <Section as={'fieldset'}>
            <Row>
              <Col xs={12}>
                <Text variant={'title'} as={'h3'}>
                  I tuoi dati
                </Text>
              </Col>

              <Col xs={12} md={6}>
                <Label>Nome</Label>
                <TextInput
                  label={'Nome'}
                  value={fields.name}
                  name={'name'}
                  onChange={handleInputChange}
                  required
                />
              </Col>

              <Col xs={12} md={6}>
                <Label>Cognome</Label>
                <TextInput
                  label={'Cognome'}
                  value={fields.surname}
                  name={'surname'}
                  onChange={handleInputChange}
                  required
                />
              </Col>

              <Col xs={8}>
                <DatePicker
                  label={'Data di nascita'}
                  onChange={handleBirthdayChange}
                  value={fields.birthday}
                />
              </Col>

              <Col xs={4}>
                <Select
                  label={'Genere'}
                  options={[
                    { value: 'm', label: 'Maschio' },
                    { value: 'f', label: 'Femmina' },
                  ]}
                  onChange={handleSexUpdate}
                  inputValue={fields.sex || ''}
                />
              </Col>

              <Col xs={9}>
                <Label>Indirizzo</Label>
                <TextInput
                  label={'Indirizzo'}
                  value={fields.address}
                  name={'address'}
                  onChange={handleInputChange}
                />
              </Col>

              <Col xs={3}>
                <Label>Civico</Label>
                <TextInput
                  label={'Civico'}
                  value={fields.houseNumber}
                  name={'houseNumber'}
                  onChange={handleInputChange}
                />
              </Col>

              <Col xs={12} md={5}>
                <Label>Città</Label>
                <TextInput
                  label={'Città'}
                  value={fields.city}
                  name={'city'}
                  onChange={handleInputChange}
                />
              </Col>

              <Col xs={12} md={4}>
                <Label>Provincia</Label>
                <ProvinceSelector
                  onChange={handleProvinceUpdate}
                  value={fields.province}
                />
              </Col>

              <Col xs={12} md={3}>
                <Label>CAP</Label>
                <TextInput
                  label={'CAP'}
                  value={fields.postalCode}
                  name={'postalCode'}
                  onChange={handleInputChange}
                />
              </Col>
            </Row>
          </Section>

          <Section as={'fieldset'}>
            <Text variant={'title'} as={'h3'}>
              Contatti
            </Text>

            <Row>
              <Col xs={12}>
                <Label>Cellulare</Label>
                <TextInput
                  label={'Cellulare'}
                  type={'tel'}
                  name={'mobile'}
                  onChange={handleInputChange}
                  value={fields.mobile}
                  pattern={'[0-9]{9,10}'}
                />
              </Col>

              <Col xs={12}>
                <Label>Email</Label>
                <TextInput
                  label={'Email'}
                  type={'email'}
                  value={fields.email}
                  name={'email'}
                  onChange={handleInputChange}
                  required
                />
              </Col>

              <Col xs={12}>
                <Label>Password</Label>
                <TextInput
                  label={'Password'}
                  type={'password'}
                  value={fields.password}
                  name={'password'}
                  onChange={handleInputChange}
                />
              </Col>
            </Row>
          </Section>

          <Preferences
            selectedFields={fields.data}
            onChange={handlePreferencesUpdate}
          />

          <Section as={'fieldset'}>
            <Text variant={'title'} as={'h3'}>
              Le mie preferenze di comunicazione
            </Text>

            <PrivacyMessage
              newsletterAccepted={
                fields.privacyChecked === '3' && fields.newsletterChecked
              }
              onChangeNewsletter={(checked) => {
                setFields((prevState) => ({
                  ...prevState,
                  privacyChecked: checked ? '3' : userData.privacyChecked,
                }));
                handleNewsletter(checked);
              }}
            />
          </Section>

          <Button
            loading={user.fetching}
            disabled={user.fetching}
            type={'submit'}
            className={classes.formButton}
          >
            Salva
          </Button>
        </Form>

        {/*
        <Button variant={'secondary'} onClick={toggleDeletionConfirmation}>
          Elimina il mio profilo
        </Button>
        */}
      </SideBar>

      <Modal
        show={showDeletionConfirmation}
        onHide={toggleDeletionConfirmation}
      >
        <Modal.Header>
          <Modal.Title>
            Sei sicuro di voler eliminare il tuo profilo?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>Questa opzione è irreversibile</Modal.Body>
        <Modal.Footer>
          <Button variant={'secondary'} onClick={toggleDeletionConfirmation}>
            Annulla
          </Button>

          <Button onClick={deleteAccount}>Conferma</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCouponOffer} onHide={toggleCouponOffer}>
        <Modal.Header>
          <Modal.Title>Grazie per aver aggiornato i tuoi dati</Modal.Title>
        </Modal.Header>

        <Modal.Body>Clicca qui per ottenere il tuo buono sconto!</Modal.Body>

        <Modal.Footer>
          <Button variant={'secondary'} onClick={toggleCouponOffer}>
            Annulla
          </Button>

          <ButtonLink to={'/coupons'}>Richiedi coupon</ButtonLink>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
